/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { ViewerElement, ViewerHotSpotResult } from '@babylonjs/viewer';
import { A3dViewer } from './A3dViewer';
import { A3D_TAGS, isA3dElementInstance } from './core';
const DEFAULT_ENV = 'https://cdn.substance3d.com/v2/files/public/studio-white.env';
/**
 * Core component of the viewer. It is responsible for rendering the 3D scene and managing the lifecycle of the viewer.
 * @element a3d-viewer
 *
 * @fires sceneloaded - Dispatched when the scene is loaded and ready to be interacted with.
 */
let A3dViewerElement = class A3dViewerElement extends ViewerElement {
    constructor() {
        super(A3dViewer);
        /**
         * @internal
         */
        this.disableInterpolate = false;
        /**
         * @internal
         */
        this.disableInertia = false;
        /**
         * @internal
         */
        this.aoStrength = 0.2;
        /**
         * Disable camera auto reframe.
         * If true, the camera will not automatically reframe the scene when a new model is loaded or removed.
         */
        this.noCameraReframe = false;
        /**
         * Disable camera auto limit.
         * If true, the camera will not be limited to the bounds of the scene.
         */
        this.noCameraLimit = false;
        /**
         * Used to check if the viewer is loaded
         */
        this.loaded = false;
        this.childrenObserverCallback = async (mutationList) => {
            console.debug('Mutation observer when children added or removed:', mutationList);
            const details = this.viewerDetails;
            if (!details) {
                return;
            }
            // TODO Removed when babylon build is fixed
            // Currently broken since 7.50.0
            // Works without on 7.49.0
            // https://github.com/BabylonJS/Babylon.js/pull/16234
            // Mark the scene as mutated to force a re-render
            if (details.viewer) {
                details.viewer.markSceneMutated();
            }
            for (const mutation of mutationList) {
                for (const addedNode of mutation.addedNodes) {
                    if (addedNode instanceof Element && isA3dElementInstance(addedNode)) {
                        await addedNode.init(details);
                        console.debug('Children linked to a3d-viewer with observer:', addedNode);
                        if (addedNode.hasChildNodes()) {
                            await this.initChildren(addedNode, details);
                        }
                    }
                }
            }
        };
    }
    async connectedCallback() {
        super.connectedCallback();
        this.addEventListener('viewerready', this.onViewerInitialized);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('viewerready', this.onViewerInitialized);
    }
    async update(changedProperties) {
        super.update(changedProperties);
        if (changedProperties.has('noCameraReframe') && this.viewerDetails) {
            this.viewerDetails.viewer.cameraReframe = !this.noCameraReframe;
        }
        if (changedProperties.has('noCameraLimit') && this.viewerDetails) {
            this.viewerDetails.viewer.cameraLimit = !this.noCameraLimit;
        }
        if (changedProperties.has('aoStrength') && this.viewerDetails) {
            this.viewerDetails.viewer.setRendererOptions('aoStrength', this.aoStrength);
        }
    }
    /**
     * Waits for all custom elements in the given set to be defined.
     * This method logs the progress every `logInterval` milliseconds.
     * @internal
     */
    async _waitForAllTagsWithProgress(tags, logInterval = 2000) {
        const pendingTags = new Set(tags);
        const start = Date.now();
        const logTimer = setInterval(() => {
            const elapsed = ((Date.now() - start) / 1000).toFixed(1);
            console.debug(`[${elapsed}s] Still waiting on elements: ${[...pendingTags].join(', ')}`);
        }, logInterval);
        try {
            await Promise.all([...tags].map((tag) => customElements.whenDefined(tag).then(() => {
                pendingTags.delete(tag);
            })));
        }
        finally {
            clearInterval(logTimer);
        }
    }
    /**
     * Waits for all A3D_TAGS custom elements in the DOM to be defined.
     * @internal
     */
    async waitForDefinedA3dTags() {
        // Wait for custom elements with localNames matching A3D_TAGS to be defined.
        const undefinedElements = this.querySelectorAll(':not(:defined)');
        console.debug('Undefined elements:', undefinedElements);
        const a3dTagNames = Object.values(A3D_TAGS);
        const tagsToWaitFor = new Set([...undefinedElements]
            .filter((el) => a3dTagNames.includes(el.localName))
            .map((el) => el.localName));
        console.debug('Undefined known a3d tags:', tagsToWaitFor);
        // Wait until all A3D custom elements are defined.
        await this._waitForAllTagsWithProgress(tagsToWaitFor);
    }
    /**
     * Initializes A3D children of the given root element in **breadth-first order**.
     *
     * @param root - Root element to start traversal from.
     * @param details - Viewer details passed to each element's `init()` method.
     * @param onlyA3dTags - If true, only initialize known A3D tag types.
     */
    async initChildren(root, details, filterCallback = () => true) {
        // Start traversal with the *direct children* of the root
        let currentLevel = Array.from(root.children);
        while (currentLevel.length > 0) {
            const nextLevel = [];
            const initPromises = [];
            for (const element of currentLevel) {
                if (filterCallback(element) && isA3dElementInstance(element)) {
                    console.debug('Children linked to a3d-viewer:', element);
                    initPromises.push(element.init(details));
                }
                // Collect this element’s children for the next level
                nextLevel.push(...Array.from(element.children));
            }
            await Promise.all(initPromises);
            console.debug('Next children level to link:', nextLevel);
            currentLevel = nextLevel;
        }
    }
    /**
     * Recursively initializes children of the given element that match A3D_TAGS.
     * @internal
     */
    async initA3dTagsChildren(root, details) {
        const tags = Object.values(A3D_TAGS);
        const filter = (el) => tags.includes(el.localName);
        await this.initChildren(root, details, filter);
    }
    /**
     * Waits for non-A3D_TAGS custom elements to be defined, then initializes those that are A3dElement instances.
     * This is done to ensure consistent initialization order.
     * @internal
     */
    async initRemainingA3dElements(details) {
        const undefinedElements = this.querySelectorAll(':not(:defined)');
        const a3dTagNames = Object.values(A3D_TAGS);
        // Extract localNames of undefined elements that are NOT part of A3D_TAGS
        const tagsToWaitFor = new Set([...undefinedElements]
            .filter((el) => !a3dTagNames.includes(el.localName))
            .map((el) => el.localName));
        console.debug('Undefined remaining elements:', tagsToWaitFor);
        // We wait for all non-A3D_TAGS custom elements to be defined to ensure consistent initialization order
        await this._waitForAllTagsWithProgress(tagsToWaitFor);
        // Now that all elements are defined, find and init those that are A3dElement instances
        const filter = (el) => !a3dTagNames.includes(el.localName);
        this.initChildren(this, details, filter);
    }
    /**
     * Initializes the viewer and its children when the viewer is ready.
     * @event viewerready
     *
     * @returns {Promise<void>}
     * @internal
     */
    async onViewerInitialized() {
        const details = this.viewerDetails;
        if (!details) {
            return;
        }
        this.viewerDetails.viewer.cameraReframe = !this.noCameraReframe;
        this.viewerDetails.viewer.cameraLimit = !this.noCameraLimit;
        this.viewerDetails.viewer.setRendererOptions('aoStrength', this.aoStrength);
        if (this.disableInertia) {
            details.camera.inertia = 0;
        }
        if (this.disableInterpolate) {
            details.viewer.disableInterpolate = true;
        }
        // Start initializing all the children
        {
            // Need to use the right handed system as we have TransformNode
            // Seems to not invert the glb if it is already in right handed system
            details.scene.useRightHandedSystem = true;
        }
        // Waits for all A3D_TAGS custom elements in the DOM to be defined.
        await this.waitForDefinedA3dTags();
        // Initializes A3D children of the given root element in **breadth-first order**.
        await this.initA3dTagsChildren(this, details);
        // Waits for non-A3D_TAGS custom elements to be defined, then initializes those that are A3dElement instances.
        this.initRemainingA3dElements(details);
        // Listen for added or removed children to initialize them
        {
            this._childrenObserver = new MutationObserver(this.childrenObserverCallback);
            const observeOptions = {
                childList: true,
                subtree: true,
            };
            this._childrenObserver.observe(this, observeOptions);
        }
        // if no env is provided, provide one by default
        if (!this.viewerDetails.scene.environmentTexture) {
            await this.viewerDetails.viewer.loadEnvironment(DEFAULT_ENV, {
                lighting: true,
                skybox: false,
            });
        }
        this.dispatchEvent(new Event('sceneloaded'));
        this.loaded = true;
    }
    _renderOverlay() {
        // NOTE: The unnamed 'slot' element holds all child elements of the <babylon-viewer> that do not specify a 'slot' attribute.
        return html `
      <slot class="full-size children-slot"></slot>
      <slot name="tool-bar"></slot>
      <slot class="loading-progress" name="loading-progress"></slot>
    `;
    }
    /**
     * Updates the camera to focus on a a3d-hot-spot element or named hot spot property.
     * @param hotSpot The a3d-hot-spot element or the name of the hot spot to focus on.
     * @returns true if the hot spot was found and the camera was updated, false otherwise.
     */
    focusHotSpot(hotSpot) {
        if (typeof hotSpot === 'string')
            return super.focusHotSpot(hotSpot);
        if (!this.viewerDetails)
            return false;
        const result = new ViewerHotSpotResult();
        const query = hotSpot.getHotSpotToRef(result);
        if (!query)
            return false;
        this.viewerDetails.viewer.pauseAnimation();
        const cameraOrbit = hotSpot.cameraOrbit ?? [undefined, undefined, undefined];
        this.viewerDetails.camera.interpolateTo(cameraOrbit[0], cameraOrbit[1], cameraOrbit[2], new Vector3(result.worldPosition[0], result.worldPosition[1], result.worldPosition[2]), undefined, this.disableInterpolate ? 0 : undefined);
        return true;
    }
};
A3dViewerElement.styles = [
    ViewerElement.styles,
    css `
      .loading-progress {
        position: absolute;
        inset: 0;
        overflow: hidden;
        display: block;
        pointer-events: none;
      }
    `,
];
__decorate([
    property({ type: Boolean, attribute: 'disable-interpolate' })
], A3dViewerElement.prototype, "disableInterpolate", void 0);
__decorate([
    property({ type: Boolean, attribute: 'disable-inertia' })
], A3dViewerElement.prototype, "disableInertia", void 0);
__decorate([
    property({ type: Number, attribute: 'ao-strength' })
], A3dViewerElement.prototype, "aoStrength", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-camera-reframe' })
], A3dViewerElement.prototype, "noCameraReframe", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-camera-limit' })
], A3dViewerElement.prototype, "noCameraLimit", void 0);
A3dViewerElement = __decorate([
    customElement('a3d-viewer')
], A3dViewerElement);
export { A3dViewerElement };
